import { reducer } from '@ardoq/rxbeach';
import { GroupsTabState, PerspectivesEditorState } from '../../types';
import {
  UpdateGroupingRulePayload,
  createGroupingRule as createGroupingRuleAction,
  deleteGroupingRule as deleteGroupingRuleAction,
  reorderGroupingRules as reorderGroupingRulesAction,
  updateGroupingRule as updateGroupingRuleAction,
  clearGroupingRules as clearGroupingRulesAction,
} from '../actions';
import {
  PerspectiveGroupingRule,
  PerspectivesGroupsOptions,
  addNewGroupingRule,
  deleteGroupingRule,
  updateGroupingRules,
} from '@ardoq/perspectives';
import {
  notifyGroupingOptionsChanged,
  appliedGroupingRulesChanged,
} from 'perspective/actions';
import { getGroupingRuleIdsNotRelevantToDataset } from '../../utils';

export const getGroupsTabInitialState: () => GroupsTabState = () => ({
  groupingRules: [],
  rulesIdsWithWarnings: [],
  groupOptions: {
    appliedGroupingRules: [],
    workspaces: [],
    componentTypesPerWorkspace: {},
    referenceTypesPerWorkspace: {},
    fieldsPerWorkspace: {},
    tagsPerWorkspace: {},
    globalComponentTypeNames: [],
    globalReferenceTypeNames: [],
  },
});

export const clearGroupingTabState = (
  state: PerspectivesEditorState
): GroupsTabState => ({
  ...state.groupsTab,
  groupingRules: [],
});

const handleCreateGroupingRule = (
  state: PerspectivesEditorState,
  newGroupingRule: PerspectiveGroupingRule
) => ({
  ...state,
  groupsTab: {
    ...state.groupsTab,
    groupingRules: addNewGroupingRule(
      state.groupsTab.groupingRules,
      newGroupingRule,
      true
    ),
  },
});

const handleUpdateGroupingRule = (
  state: PerspectivesEditorState,
  { updatedGroupingRuleId, updatedGroupingRule }: UpdateGroupingRulePayload
): PerspectivesEditorState => ({
  ...state,
  groupsTab: {
    ...state.groupsTab,
    groupingRules: updateGroupingRules(
      state.groupsTab.groupingRules,
      updatedGroupingRuleId,
      updatedGroupingRule
    ),
  },
});

const handleReorderGroupingRules = (
  state: PerspectivesEditorState,
  reorderedGroupingRules: PerspectiveGroupingRule[]
) => ({
  ...state,
  groupsTab: {
    ...state.groupsTab,
    groupingRules: reorderedGroupingRules,
  },
});

const handleDeleteGroupingRule = (
  state: PerspectivesEditorState,
  deletedGroupingRuleId: string
) => ({
  ...state,
  groupsTab: {
    ...state.groupsTab,
    groupingRules: deleteGroupingRule(
      state.groupsTab.groupingRules,
      deletedGroupingRuleId
    ),
  },
});

/**
 * notifyGroupingOptionsChange is emitted by the postMessageBridge and informs that the groupingOptions have changed.
 * This only updates the groupOptions metadata, NOT the UI rules.
 */
const handleNotifyGroupingOptionsChange = (
  state: PerspectivesEditorState,
  groupsOptions: PerspectivesGroupsOptions
) => ({
  ...state,
  groupsTab: {
    ...state.groupsTab,
    groupOptions: groupsOptions,
  },
});

/**
 * handleAppliedGroupingRulesChanged is dispatched when the applied grouping rules have actually changed.
 * This updates the UI rules (groupingRules).
 */
const handleAppliedGroupingRulesChanged = (
  state: PerspectivesEditorState,
  groupsOptions: PerspectivesGroupsOptions
) => {
  const nextGroupingRules = groupsOptions.appliedGroupingRules;

  return {
    ...state,
    groupsTab: {
      ...state.groupsTab,
      groupingRules: nextGroupingRules,
      groupOptions: groupsOptions,
      rulesIdsWithWarnings: getGroupingRuleIdsNotRelevantToDataset(
        nextGroupingRules,
        state.datasetRelatedData
      ),
    },
  };
};

const handleClearGroupingRules = (
  state: PerspectivesEditorState
): PerspectivesEditorState => ({
  ...state,
  groupsTab: clearGroupingTabState(state),
});

export const groupTabReducers = [
  reducer(notifyGroupingOptionsChanged, handleNotifyGroupingOptionsChange),
  reducer(appliedGroupingRulesChanged, handleAppliedGroupingRulesChanged),
  reducer(createGroupingRuleAction, handleCreateGroupingRule),
  reducer(updateGroupingRuleAction, handleUpdateGroupingRule),
  reducer(reorderGroupingRulesAction, handleReorderGroupingRules),
  reducer(deleteGroupingRuleAction, handleDeleteGroupingRule),
  reducer(clearGroupingRulesAction, handleClearGroupingRules),
];
